// IPFS Gateway Fallback Utility
// Provides multiple gateway options for faster IPFS image loading

// List of IPFS gateways (ordered by reliability/speed)
const IPFS_GATEWAYS = [
  'https://gateway.pinata.cloud',
  'https://ipfs.io',
  'https://cloudflare-ipfs.com',
  'https://dweb.link',
  'https://gateway.ipfs.io',
];

/**
 * Extract IPFS hash from various URL formats
 * @param {string} url - IPFS URL (can be pinata.cloud, ipfs://, or gateway URL)
 * @returns {string|null} - IPFS hash (CID)
 */
export const extractIPFSHash = (url) => {
  if (!url) return null;
  
  // Handle ipfs:// protocol
  if (url.startsWith('ipfs://')) {
    return url.replace('ipfs://', '').split('/')[0];
  }
  
  // Handle pinata.cloud URLs
  if (url.includes('pinata.cloud')) {
    const match = url.match(/\/ipfs\/([^/?]+)/);
    if (match) return match[1];
    // Also handle direct hash URLs
    const hashMatch = url.match(/(Qm[a-zA-Z0-9]{44}|baf[a-zA-Z0-9]{56,})/);
    if (hashMatch) return hashMatch[1];
  }
  
  // Handle gateway URLs (ipfs.io, cloudflare-ipfs.com, etc.)
  const gatewayMatch = url.match(/\/ipfs\/([^/?]+)/);
  if (gatewayMatch) return gatewayMatch[1];
  
  // Try to extract any IPFS hash pattern
  const hashPattern = url.match(/(Qm[a-zA-Z0-9]{44}|baf[a-zA-Z0-9]{56,})/);
  if (hashPattern) return hashPattern[1];
  
  return null;
};

/**
 * Convert IPFS URL to use a specific gateway
 * @param {string} url - Original IPFS URL
 * @param {string} gateway - Gateway base URL
 * @returns {string} - Gateway URL
 */
export const convertToGateway = (url, gateway) => {
  if (!url) return url;
  
  const hash = extractIPFSHash(url);
  if (!hash) return url; // Return original if we can't extract hash
  
  // Ensure gateway ends with /
  const gatewayBase = gateway.endsWith('/') ? gateway : `${gateway}/`;
  return `${gatewayBase}ipfs/${hash}`;
};

/**
 * Get all gateway URLs for an IPFS hash
 * @param {string} url - Original IPFS URL
 * @returns {string[]} - Array of gateway URLs
 */
export const getAllGatewayUrls = (url) => {
  const hash = extractIPFSHash(url);
  if (!hash) return [url]; // Return original if we can't extract hash
  
  return IPFS_GATEWAYS.map(gateway => {
    const gatewayBase = gateway.endsWith('/') ? gateway : `${gateway}/`;
    return `${gatewayBase}ipfs/${hash}`;
  });
};

/**
 * Get the best gateway URL for an IPFS hash
 * Returns the first gateway URL (Pinata is usually fastest)
 * The Image element will handle fallback automatically on error
 * @param {string} url - Original IPFS URL
 * @returns {string} - Best gateway URL to try first
 */
export const getBestGatewayUrl = (url) => {
  // Cloudinary and other non-IPFS URLs return as-is
  if (!url || url.includes('cloudinary.com') || (!url.includes('ipfs') && !url.includes('pinata.cloud'))) {
    return url;
  }
  
  const hash = extractIPFSHash(url);
  if (!hash) return url;
  
  // Return Pinata gateway first (usually fastest)
  return `${IPFS_GATEWAYS[0]}/ipfs/${hash}`;
};

/**
 * Get all gateway URLs for fallback
 * @param {string} url - Original IPFS URL
 * @returns {string[]} - Array of gateway URLs to try in order
 */
export const getGatewayUrlsForFallback = (url) => {
  // Cloudinary and other non-IPFS URLs return as-is
  if (!url || url.includes('cloudinary.com') || (!url.includes('ipfs') && !url.includes('pinata.cloud'))) {
    return [url];
  }
  
  return getAllGatewayUrls(url);
};

/**
 * Check if URL is an IPFS URL
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isIPFSUrl = (url) => {
  if (!url) return false;
  if (url.includes('cloudinary.com')) return false;
  return url.includes('ipfs') || url.includes('pinata.cloud') || url.startsWith('ipfs://');
};

const ipfsGatewayUtils = {
  extractIPFSHash,
  convertToGateway,
  getAllGatewayUrls,
  getBestGatewayUrl,
  getGatewayUrlsForFallback,
  isIPFSUrl,
};

export default ipfsGatewayUtils;

